/**
 * useCodeExecution - Hook for executing code
 */

import { useState, useCallback } from 'react';
import { codeExecutor } from '../services/CodeExecutor';

export function useCodeExecution() {
  const [output, setOutput] = useState('');
  const [error, setError] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [executionTime, setExecutionTime] = useState(0);
  const [history, setHistory] = useState([]);

  const execute = useCallback(async (code, stdin = '') => {
    setIsRunning(true);
    setError(null);
    setOutput('');

    try {
      const result = await codeExecutor.execute(code, stdin);
      
      setOutput(result.output || '');
      setError(result.error);
      setExecutionTime(result.executionTime);

      // Add to history
      const historyEntry = {
        code,
        stdin,
        output: result.output,
        error: result.error,
        success: result.success,
        executionTime: result.executionTime,
        timestamp: Date.now()
      };
      setHistory(prev => [historyEntry, ...prev.slice(0, 9)]);

      setIsRunning(false);
      return result;
    } catch (err) {
      const errorResult = {
        success: false,
        output: '',
        error: err.message,
        executionTime: 0
      };
      setError(err.message);
      setIsRunning(false);
      return errorResult;
    }
  }, []);

  const executeForLanguage = useCallback(async (language, code, stdin = '', options = {}) => {
    setIsRunning(true);
    setError(null);
    setOutput('');

    try {
      const result = await codeExecutor.executeForLanguage(language, code, stdin, options);
      
      setOutput(result.output || '');
      setError(result.error);
      setExecutionTime(result.executionTime);
      setIsRunning(false);
      return result;
    } catch (err) {
      setError(err.message);
      setIsRunning(false);
      return { success: false, error: err.message };
    }
  }, []);

  const runTestCases = useCallback(async (code, testCases, language = null) => {
    setIsRunning(true);
    try {
      const results = await codeExecutor.runTestCases(code, testCases, language);
      setIsRunning(false);
      return results;
    } catch (err) {
      setIsRunning(false);
      return { totalTests: 0, passed: 0, failed: 0, results: [], error: err.message };
    }
  }, []);

  const clearOutput = useCallback(() => {
    setOutput('');
    setError(null);
    setExecutionTime(0);
  }, []);

  const clearHistory = useCallback(() => {
    setHistory([]);
  }, []);

  return {
    output,
    error,
    isRunning,
    executionTime,
    history,
    
    // Actions
    execute,
    executeForLanguage,
    runTestCases,
    clearOutput,
    clearHistory,
    
    // Status
    hasOutput: output.length > 0,
    hasError: error !== null,
    isReady: codeExecutor.isReady(),
    executorInfo: codeExecutor.getExecutorInfo()
  };
}

export default useCodeExecution;